import { z } from 'zod';
export const historyWindows = ['15m', '1h', '6h', '24h'];
// Inspection uses an existing terminal grant, but accepts only these read-only operations.
export const serverInspectionRequest = z.discriminatedUnion('section', [
    z.object({ section: z.literal('resources') }).strict(),
    z.object({ section: z.literal('processes') }).strict(),
    z.object({ section: z.literal('services') }).strict(),
    z
        .object({
        section: z.literal('logs'),
        unit: z
            .string()
            .regex(/^[A-Za-z0-9][A-Za-z0-9_.@:-]{0,179}\.service$/)
            .optional(),
        minutes: z.union([z.literal(15), z.literal(60), z.literal(1440)]),
        priority: z.enum(['all', 'warning', 'error']),
    })
        .strict(),
    // Hosts that list 'history' in resources.capabilities keep a bounded in-memory ring.
    z.object({ section: z.literal('history'), window: z.enum(historyWindows) }).strict(),
]);
const count = z.number().finite().nonnegative();
const percent = z.number().finite().min(0).max(100);
const label = (max) => z.string().max(max);
const command = z.object({
    started: z.boolean(),
    exit_code: z.number().nullable(),
    stdout: z.string().max(32768),
    stderr: z.string().max(32768),
    timed_out: z.boolean(),
    truncated: z.boolean(),
    output_incomplete: z.boolean(),
});
const common = { version: z.literal(1), sampledAt: count, executionUid: count };
/** Linux pressure-stall averages (percent of wall time over 10/60/300 seconds). */
const stall = z.object({ avg10: percent, avg60: percent, avg300: percent });
const pressure = z.object({ some: stall, full: stall.optional() });
/** Everything added after the first release is optional: older hosts omit it. */
const address = z.object({
    address: label(45),
    prefix: z.number().int().min(0).max(128),
    family: z.union([z.literal(4), z.literal(6)]),
    scope: z.enum(['global', 'private', 'link', 'host']),
});
const networkInterface = z.object({
    name: label(64),
    received: count,
    sent: count,
    kind: z
        .enum(['loopback', 'physical', 'wireless', 'bridge', 'bond', 'vlan', 'virtual', 'tunnel'])
        .optional(),
    state: label(16).optional(),
    speedMbps: count.nullable().optional(),
    defaultRoute: z
        .array(z.union([z.literal(4), z.literal(6)]))
        .max(2)
        .optional(),
    addresses: z.array(address).max(16).optional(),
    /** Bond or bridge this interface is enslaved to. */
    master: label(64).nullable().optional(),
});
const diskHealth = z.object({
    source: z.enum(['udisks-nvme', 'udisks-ata']),
    criticalWarnings: z.array(label(32)).max(16),
    failing: z.boolean().optional(),
    percentUsed: count.optional(),
    availableSpare: percent.optional(),
    spareThreshold: percent.optional(),
    mediaErrors: count.optional(),
    powerOnHours: count.optional(),
    unsafeShutdowns: count.optional(),
    temperatureC: z.number().finite().optional(),
    warningTempMinutes: count.optional(),
    criticalTempMinutes: count.optional(),
    dataWrittenBytes: count.optional(),
    updatedAt: count.optional(),
});
const disk = z.object({
    name: label(32),
    kind: z.enum(['nvme', 'ssd', 'hdd', 'virtual', 'raid', 'mmc']),
    model: label(64).nullable(),
    firmware: label(32).nullable(),
    sizeBytes: count,
    /** Cumulative /proc/diskstats counters; the client derives rates between readings. */
    reads: count,
    readSectors: count,
    readMs: count,
    writes: count,
    writeSectors: count,
    writeMs: count,
    busyMs: count,
    inFlight: count,
    temperatureC: z.number().finite().nullable(),
    health: diskHealth.nullable(),
    /** Why health is missing, when the host could tell (for example 'udisks_unavailable'). */
    healthUnavailable: label(48).optional(),
});
const filesystem = z.object({
    mount: label(256),
    source: label(128),
    type: label(32),
    disk: label(32).nullable(),
    total: count,
    used: count,
    available: count,
    inodesTotal: count,
    inodesFree: count,
    readOnly: z.boolean(),
});
export const serverInspectionResponse = z.discriminatedUnion('section', [
    z.object({
        ...common,
        section: z.literal('resources'),
        /** Optional features this host implements, such as 'history'. */
        capabilities: z.array(label(32)).max(16).optional(),
        cpu: z
            .object({
            total: count,
            idle: count,
            cores: count,
            iowait: count.optional(),
            /** Per logical CPU [total, idle] jiffies, in /proc/stat order. */
            perCore: z
                .array(z.tuple([count, count]))
                .max(1024)
                .optional(),
        })
            .nullable(),
        cpuInfo: z
            .object({
            model: label(96).nullable(),
            sockets: count.nullable(),
            physicalCores: count.nullable(),
            threads: count,
        })
            .optional(),
        memory: z.record(z.string(), count),
        vm: z.object({ swapIn: count, swapOut: count, majorFaults: count }).optional(),
        pressure: z
            .object({ cpu: pressure.optional(), memory: pressure.optional(), io: pressure.optional() })
            .optional(),
        uptime: count.nullable(),
        load: z.array(count).max(3),
        network: z.array(networkInterface).max(32),
        disks: z.array(disk).max(16).optional(),
        filesystems: z.array(filesystem).max(16).optional(),
        storage: command,
    }),
    z.object({
        ...common,
        section: z.literal('processes'),
        output: command,
        /** Structured rows from /proc: names only, never command lines or environments. */
        processes: z
            .array(z.object({
            pid: z.number().int().positive(),
            name: label(64),
            user: label(32),
            state: label(1),
            /** Percent of one logical CPU over cpuWindowMs; null until two readings exist. */
            cpu: count.nullable(),
            rss: count,
            threads: count,
            runtimeSeconds: count.nullable(),
        }))
            .max(100)
            .optional(),
        cpuWindowMs: count.nullable().optional(),
        processCount: count.optional(),
    }),
    z.object({ ...common, section: z.literal('services'), output: command }),
    z.object({ ...common, section: z.literal('logs'), output: command }),
    z.object({
        ...common,
        section: z.literal('history'),
        window: z.enum(historyWindows),
        stepSeconds: z.number().int().positive(),
        /** Timestamp (ms) of the first point; points are stepSeconds apart. */
        start: count,
        /** When the host began sampling; earlier points are unknown, not zero. */
        recordingSince: count,
        series: z.object({
            cpu: z.array(percent.nullable()).max(400),
            memory: z.array(percent.nullable()).max(400),
            swap: z.array(percent.nullable()).max(400),
            load: z.array(count.nullable()).max(400),
            memoryPressure: z.array(percent.nullable()).max(400),
            diskRead: z.array(count.nullable()).max(400),
            diskWrite: z.array(count.nullable()).max(400),
            netRx: z.array(count.nullable()).max(400),
            netTx: z.array(count.nullable()).max(400),
        }),
    }),
]);
//# sourceMappingURL=server-inspection.js.map